import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useSearchUser, useUpdateWinner } from './hooks';

type Props = {
  open: boolean;
  onClose: () => void;
  winner: any;
  raffle: any;
  onSuccess?: () => void;
};

const UpdateWinnerModal = ({ open, onClose, winner, raffle, onSuccess }: Props) => {
  const [userId, setUserId] = useState('');
  const [searchEnabled, setSearchEnabled] = useState(false);

  const { data: foundUser, isFetching: isSearching } = useSearchUser(userId, searchEnabled);

  const handleClose = () => {
    setUserId('');
    setSearchEnabled(false);
    onClose();
  };

  const updateWinner = useUpdateWinner(() => {
    onSuccess?.();
    handleClose();
  });

  const handleSearch = () => {
    if (!userId.trim()) return;
    setSearchEnabled(true);
  };

  const handleUserIdChange = (val: string) => {
    setUserId(val);
    setSearchEnabled(false);
  };

  const handleSubmit = () => {
    if (!userId || !foundUser) return;
    updateWinner.mutate({
      raffleId: raffle?.id,
      place: winner?.place,
      userId: Number(userId),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <DialogContent sx={{ position: 'relative', px: 3, pt: 3, pb: 3 }}>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: 'absolute', top: 12, right: 12, color: '#9CA3AF' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Typography fontSize={18} fontWeight={700} color="#111827" mb={2.5}>
          Update Winner
        </Typography>

        <Stack direction="row" justifyContent="space-between" mb={2.5}>
          <Typography fontSize={14} fontWeight={700} color="#333B69">
            Place No.:{' '}
            <Typography component="span" color="#718EBF" fontWeight={700}>
              {winner?.place ?? '-'}
            </Typography>
          </Typography>
          <Typography fontSize={14} fontWeight={700} color="#333B69">
            Draw No.:{' '}
            <Typography component="span" color="#718EBF" fontWeight={700}>
              {raffle?.code ?? '-'}
            </Typography>
          </Typography>
        </Stack>

        <Stack direction="row" gap={1.5} mb={3}>
          <TextField
            placeholder="Search User ID"
            value={userId}
            onChange={(e) => handleUserIdChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            size="small"
            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleSearch} disabled={isSearching}>
                    {isSearching
                      ? <CircularProgress size={16} />
                      : <SearchIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
                    }
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            placeholder="Display Name"
            value={foundUser?.displayName ?? ''}
            size="small"
            inputProps={{ readOnly: true }}
            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13, bgcolor: '#F9FAFB' } }}
          />
          <TextField
            placeholder="Player Entries"
            value={foundUser?.totalRedeemedTickets ?? ''}
            size="small"
            inputProps={{ readOnly: true }}
            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13, bgcolor: '#F9FAFB' } }}
          />
        </Stack>

        <Box textAlign="center">
          <Button
            variant="contained"
            disabled={!userId || !foundUser || updateWinner.isPending}
            onClick={handleSubmit}
            sx={{
              width: 220,
              height: 50,
              borderRadius: '10px',
              fontWeight: 700,
              fontSize: 14,
              bgcolor: '#6A00AC',
              '&:hover': { bgcolor: '#520088' },
            }}
          >
            {updateWinner.isPending ? 'SUBMITTING...' : 'SUBMIT'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateWinnerModal;
